import { assertNotEmpty } from '@festina-life/core'
import { type ComponentProps, type ReactNode, useMemo, useState } from 'react'

import { RawTextInput } from '../../forms/textInput/RawTextInput'
import { Icon } from '../../structure/icon/Icon'
import { Modal } from '../../structure/modal/Modal'
import { type TabItem, Tabs } from '../../structure/tabs/Tabs'
import { SearchResults } from './SearchResults'
import type { PreviewProps, SearchCategory, SearchResult } from './types'
import { useSearchTabNavigation } from './useSearchTabNavigation'

type Props<Value, MatchData, Category extends string> = {
  categories: SearchCategory<Value, MatchData, Category>[]
  onSelect: (
    value: SearchResult<Value, MatchData, Category>,
    matchIndex?: number,
  ) => void
  minQueryLength?: number
  modalClassName?: string
  debounceMilliseconds?: number
} & Pick<ComponentProps<typeof Modal>, 'isOpen' | 'onClose'> &
  Pick<ComponentProps<typeof Modal.Title>, 'title'> &
  (
    | {
        isShowingPreview: true
        renderPreview: (
          props: PreviewProps<Value, MatchData, Category>,
        ) => ReactNode
      }
    | {
        isShowingPreview: false
        renderPreview?: never
      }
  )

export const SearchModal = <
  Value,
  MatchData,
  Category extends string = string,
>({
  title,
  categories,
  modalClassName,
  onSelect,
  minQueryLength = 3,
  debounceMilliseconds = 300,
  isOpen,
  onClose,
  ...other
}: Props<Value, MatchData, Category>) => {
  const tabItems: TabItem[] = useMemo(
    () =>
      categories.map((category) => ({
        key: category.key,
        title: category.label,
      })),
    [categories],
  )
  const [currentTabIndex, setCurrentTabIndex] = useSearchTabNavigation({
    tabCount: tabItems.length,
    isListeningToTabKey: isOpen,
  })
  const [query, setQuery] = useState('')
  const currentCategory = categories.at(currentTabIndex)

  const handleItemSelected = (
    item: SearchResult<Value, MatchData, Category>,
    matchIndex?: number,
  ) => {
    onSelect(item, matchIndex)
    onClose()
  }

  assertNotEmpty(currentCategory)

  return (
    <Modal
      isGlobal
      panelClassName={modalClassName}
      shouldUnmountChildrenOnClose={false}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Title title={title} />
      <Modal.Body innerClassName="min-h-0 grow">
        <Tabs
          items={tabItems}
          selectedIndex={currentTabIndex}
          onChange={(nextTab) => setCurrentTabIndex(nextTab)}
          shouldRenderEmptyChildren
        />
        <div className="shrink-0">
          <RawTextInput
            name="search"
            addOnProps={{
              element: <Icon name="MagnifyingGlass" />,
            }}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
            autoComplete="off"
          />
        </div>
        <SearchResults
          isOpen={isOpen}
          key={currentCategory.key}
          query={query}
          category={currentCategory}
          onSelect={handleItemSelected}
          minQueryLength={minQueryLength}
          debounceMilliseconds={debounceMilliseconds}
          {...other}
        />
      </Modal.Body>
    </Modal>
  )
}
